import { useState, useEffect } from 'react';
import { carouselSlideService, CarouselSlide } from '../services/api';
import { Upload, ChevronUp, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CarouselManagementPage = () => {
  const { t } = useTranslation();
  const [slides, setSlides] = useState<CarouselSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    label: '',
    heading: '',
    description: '',
    buttonText: '',
    buttonLink: '',
    gradient: 'from-blue-500 to-blue-700',
    displayOrder: 0,
    isActive: true,
    imageAlt: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      setLoading(true);
      const response = await carouselSlideService.getAdminCarouselSlides();
      setSlides(response.data.data.slides || []);
    } catch (error) {
      console.error('Failed to fetch carousel slides:', error);
      setError(t('carouselManagement.successDelete')); // Note: This might be incorrect mapping, but original was hardcoded generic string. Let's use a generic error if available, or just fallback
      setError('Failed to load carousel slides'); // Leaving as english for error log or add key
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError(t('carouselManagement.form.sizeLimit'));
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const data = new FormData();
      if (imageFile) {
        data.append('image', imageFile);
      } else if (!editingId) {
        setError('Image is required');
        setSubmitting(false);
        return;
      }

      data.append('label', formData.label);
      data.append('heading', formData.heading);
      data.append('description', formData.description);
      data.append('buttonText', formData.buttonText);
      data.append('buttonLink', formData.buttonLink);
      data.append('gradient', formData.gradient);
      data.append('displayOrder', formData.displayOrder.toString());
      data.append('isActive', formData.isActive.toString());
      data.append('imageAlt', formData.imageAlt || formData.heading);

      if (editingId) {
        await carouselSlideService.updateCarouselSlide(editingId, data);
        setSuccess(t('carouselManagement.successUpdate'));
      } else {
        await carouselSlideService.createCarouselSlide(data);
        setSuccess(t('carouselManagement.successCreate'));
      }

      resetForm();
      fetchSlides();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to save carousel slide');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (slide: CarouselSlide) => {
    setEditingId(slide.id);
    setFormData({
      label: slide.label,
      heading: slide.heading,
      description: slide.description,
      buttonText: slide.buttonText,
      buttonLink: slide.buttonLink,
      gradient: slide.gradient,
      displayOrder: slide.displayOrder ?? 0,
      isActive: slide.isActive || false,
      imageAlt: slide.imageAlt,
    });
    setImagePreview(slide.image);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm(t('carouselManagement.confirmDelete'))) return;

    try {
      await carouselSlideService.deleteCarouselSlide(id);
      setSuccess(t('carouselManagement.successDelete'));
      fetchSlides();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to delete carousel slide');
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newSlides = [...slides];
    [newSlides[index], newSlides[index - 1]] = [newSlides[index - 1], newSlides[index]];

    const slideOrders = newSlides.map((slide, idx) => ({
      id: slide.id,
      displayOrder: idx,
    }));

    try {
      await carouselSlideService.reorderSlides(slideOrders);
      setSlides(newSlides);
      setSuccess('Slides reordered successfully!');
      setTimeout(() => setSuccess(''), 2000);
    } catch (error) {
      setError('Failed to reorder slides');
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === slides.length - 1) return;
    const newSlides = [...slides];
    [newSlides[index], newSlides[index + 1]] = [newSlides[index + 1], newSlides[index]];

    const slideOrders = newSlides.map((slide, idx) => ({
      id: slide.id,
      displayOrder: idx,
    }));

    try {
      await carouselSlideService.reorderSlides(slideOrders);
      setSlides(newSlides);
      setSuccess('Slides reordered successfully!');
      setTimeout(() => setSuccess(''), 2000);
    } catch (error) {
      setError('Failed to reorder slides');
    }
  };

  const resetForm = () => {
    setFormData({
      label: '',
      heading: '',
      description: '',
      buttonText: '',
      buttonLink: '',
      gradient: 'from-blue-500 to-blue-700',
      displayOrder: slides.length,
      isActive: true,
      imageAlt: '',
    });
    setEditingId(null);
    setShowForm(false);
    setImagePreview('');
    setImageFile(null);
  };

  const gradientOptions = [
    { value: 'from-blue-500 to-blue-700', label: 'Blue' },
    { value: 'from-purple-500 to-blue-600', label: 'Purple to Blue' },
    { value: 'from-teal-500 to-blue-600', label: 'Teal to Blue' },
    { value: 'from-green-500 to-emerald-600', label: 'Green' },
    { value: 'from-orange-500 to-red-600', label: 'Orange to Red' },
    { value: 'from-pink-500 to-purple-600', label: 'Pink to Purple' },
    { value: 'from-blue-600 to-indigo-700', label: 'Blue to Indigo' },
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('carouselManagement.title')}</h1>
          <button
            onClick={() => {
              resetForm();
              setShowForm(!showForm);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {showForm ? t('common.cancel') : t('carouselManagement.newSlide')}
          </button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
            {error}
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">
              {editingId ? t('carouselManagement.editSlide') : t('carouselManagement.createSlide')}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('carouselManagement.form.image')} {editingId && t('carouselManagement.form.imageHelp')}
                </label>
                <div className="flex items-start gap-4">
                  <label className="flex flex-col items-center justify-center w-64 h-40 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="w-10 h-10 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">{t('carouselManagement.form.uploadHelp')}</p>
                        <p className="text-xs text-gray-400">{t('carouselManagement.form.sizeLimit')}</p>
                      </div>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </label>
                  {imagePreview && (
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview('');
                        setImageFile(null);
                      }}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      {t('carouselManagement.form.removeImage')}
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('carouselManagement.form.label')}
                  </label>
                  <input
                    type="text"
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('carouselManagement.form.labelPlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('carouselManagement.form.heading')}
                  </label>
                  <input
                    type="text"
                    value={formData.heading}
                    onChange={(e) => setFormData({ ...formData, heading: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('carouselManagement.form.headingPlaceholder')}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('carouselManagement.form.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('carouselManagement.form.descPlaceholder')}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('carouselManagement.form.buttonText')}
                  </label>
                  <input
                    type="text"
                    value={formData.buttonText}
                    onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('carouselManagement.form.buttonPlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('carouselManagement.form.buttonLink')}
                  </label>
                  <input
                    type="text"
                    value={formData.buttonLink}
                    onChange={(e) => setFormData({ ...formData, buttonLink: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('carouselManagement.form.linkPlaceholder')}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('carouselManagement.form.gradient')}
                  </label>
                  <select
                    value={formData.gradient}
                    onChange={(e) => setFormData({ ...formData, gradient: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {gradientOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('carouselManagement.form.displayOrder')}
                  </label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) =>
                      setFormData({ ...formData, displayOrder: parseInt(e.target.value) })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('carouselManagement.form.status')}
                  </label>
                  <select
                    value={formData.isActive ? 'active' : 'inactive'}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.value === 'active' })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="active">{t('carouselManagement.form.active')}</option>
                    <option value="inactive">{t('carouselManagement.form.inactive')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('carouselManagement.form.imageAlt')}
                </label>
                <input
                  type="text"
                  value={formData.imageAlt}
                  onChange={(e) => setFormData({ ...formData, imageAlt: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('carouselManagement.form.altPlaceholder')}
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                >
                  {submitting ? t('carouselManagement.form.saving') : editingId ? t('carouselManagement.form.update') : t('carouselManagement.form.save')}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t('carouselManagement.form.cancel')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Slides List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">{t('carouselManagement.currentSlides')}</h2>
            {loading ? (
              <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>
            ) : slides.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {t('carouselManagement.noSlides')}
              </div>
            ) : (
              <div className="space-y-4">
                {slides.map((slide, index) => (
                  <div
                    key={slide.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      {/* Image */}
                      <img
                        src={slide.image}
                        alt={slide.imageAlt}
                        className="w-32 h-20 object-cover rounded"
                      />

                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-gray-900">{slide.heading}</h3>
                              <span
                                className={`px-2 py-0.5 text-xs font-medium rounded-full ${slide.isActive
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                                  }`}
                              >
                                {slide.isActive ? t('carouselManagement.form.active') : t('carouselManagement.form.inactive')}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{slide.description}</p>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span>Label: {slide.label}</span>
                              <span>Button: {slide.buttonText}</span>
                              <span>Link: {slide.buttonLink}</span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleMoveUp(index)}
                              disabled={index === 0}
                              className="p-1 text-gray-600 hover:text-blue-600 disabled:text-gray-300"
                              title="Move up"
                            >
                              <ChevronUp className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleMoveDown(index)}
                              disabled={index === slides.length - 1}
                              className="p-1 text-gray-600 hover:text-blue-600 disabled:text-gray-300"
                              title="Move down"
                            >
                              <ChevronDown className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleEdit(slide)}
                              className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                            >
                              {t('common.edit')}
                            </button>
                            <button
                              onClick={() => handleDelete(slide.id)}
                              className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                            >
                              {t('common.delete')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarouselManagementPage;
